import { useUpdateMenuRowOrder } from '@jane/business-admin/data-access';
import type { UpdateMenuRowOrderBody } from '@jane/business-admin/data-access';
import { useStoreId } from '@jane/business-admin/hooks';
import type { MenuRow } from '@jane/business-admin/types';
import { ModalNames, SettingNames } from '@jane/business-admin/util';
import { useStoreHasGoldConfig } from '@jane/shared/hooks';

import { ArrangeModal } from './ArrangeModal';

const isCustomMenuRow = (row: MenuRow) => {
  return 'id' in row;
};

export const ArrangeMenuRowsModal = ({
  closeModal,
}: {
  closeModal: () => void;
}) => {
  const storeId = useStoreId();
  const showGold = useStoreHasGoldConfig(storeId);
  const { mutateAsync: updateMenuRowOrder, isSuccess: updateRowOrderSuccess } =
    useUpdateMenuRowOrder(storeId);

  return (
    <ArrangeModal<MenuRow, UpdateMenuRowOrderBody>
      allowDisablingForItem={(row: MenuRow) => isCustomMenuRow(row)}
      closeModal={closeModal}
      enabledKey="enabled"
      filterMenuItems={(rows) =>
        rows.filter(
          (row) =>
            !row.is_category && (row.row_type !== 'brand_specials' || showGold)
        )
      }
      formatItem={(row: {
        id: string;
        item: MenuRow;
      }): UpdateMenuRowOrderBody[number] => ({
        id: row.item.id,
        enabled: row.item.enabled,
        // For magic_row, use custom_display_name since each magic row has a unique name
        product_type:
          row.item.row_type === 'magic_row'
            ? row.item.custom_display_name || row.item.row_type
            : (row.item.row_type ?? ''),
        ranking: row.item.ranking,
      })}
      getItemEnabled={(row) => Boolean(row.enabled)}
      getItemId={(row: MenuRow) =>
        isCustomMenuRow(row) ? `${row.id}` : (row.row_type ?? '')
      }
      menuDataKey="menu_rows"
      modalName={ModalNames.ArrangeMenuRows}
      rankKey="ranking"
      settingName={SettingNames.MenuRowOrder}
      showHiddenIconForItem={(row: MenuRow) =>
        isCustomMenuRow(row) && !row.enabled
      }
      type="row"
      updateMenuOrder={updateMenuRowOrder}
      updateMenuOrderSuccess={updateRowOrderSuccess}
    />
  );
};
